import { useState } from 'react'
import { ChevronRightIcon, MessageCircleIcon } from 'lucide-react'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/useIsMobile'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { trackInquiryOpened, trackInquirySubmitted } from '@/lib/track'
import { LocationInquiryForm } from './LocationInquiryForm'

interface LocationInquirySheetProps {
  accommodation: AccommodationDetail
  renderTrigger?: (openSheet: () => void) => React.ReactNode
}

export function LocationInquirySheet({ accommodation, renderTrigger }: LocationInquirySheetProps) {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  const handleOpen = () => {
    trackInquiryOpened(accommodation)
    setOpen(true)
  }

  const handleSubmitted = () => {
    trackInquirySubmitted(accommodation)
  }

  const triggerButton = renderTrigger ? (
    renderTrigger(handleOpen)
  ) : (
    <button
      type="button"
      onClick={handleOpen}
      className="flex w-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
    >
      <MessageCircleIcon className="size-5 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-600">Mám dotaz na ubytování</div>
        <div className="truncate text-xs text-slate-400">Zeptejte se na cokoli, rádi poradíme.</div>
      </div>
      <ChevronRightIcon className="size-4 shrink-0 text-slate-400" />
    </button>
  )

  const title = 'Dotaz na ubytování'
  const description = `Napište nám, co potřebujete vědět o ubytování „${accommodation.name}“. Zprávu předáme ubytovateli a ozveme se vám na uvedený kontakt.`

  if (isMobile) {
    return (
      <>
        {triggerButton}
        <Drawer open={open} onOpenChange={setOpen} repositionInputs={false}>
          <DrawerContent className="max-h-[90dvh]">
            <DrawerHeader>
              <DrawerTitle>{title}</DrawerTitle>
              <DrawerDescription>{description}</DrawerDescription>
            </DrawerHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
              <LocationInquiryForm
                accommodation={accommodation}
                onSent={handleSubmitted}
                onClose={() => setOpen(false)}
              />
            </div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <>
      {triggerButton}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
          <SheetHeader className="border-b border-slate-100 p-6">
            <SheetTitle className="text-xl">{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <LocationInquiryForm
              accommodation={accommodation}
              onSent={handleSubmitted}
              onClose={() => setOpen(false)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
